import React from 'react'
import { usePage, Link, Head } from '@inertiajs/react'
import { ToastProvider } from '@/components/ToastProvider'
import { useBrand } from '@/hooks/useBrand'
import AccountSwitcher from '@/components/AccountSwitcher'
import UserMenu from '@/components/UserMenu'
import JumpToProperty from '@/components/JumpToProperty'
// import NotificationBell from '@/components/NotificationBell' // TEMP (hidden 2026-06-19): notifications bell hidden in both headers — re-enable with the <NotificationBell> below
import FlashHandler from '@/components/FlashHandler'
import BillingAlertStrip from '@/components/BillingAlertStrip'
import SpotlightAnnouncement from '@/components/SpotlightAnnouncement'
import type { AccountPropertyGroup, CurrentAccount, BillingAlert, AnnouncementData } from '@/types/shared'

interface PortfolioLayoutProps {
  currentUser: { id: number; email: string }
  // tenancy-14: account-grouped shape. Defaulted to [] so a briefly-absent prop never crashes.
  properties?: AccountPropertyGroup[]
  /**
   * Tab title — the page's LEAF name only ("Billing", "Team"). The " · {brand}" suffix is
   * appended once, by the `title` callback in entrypoints/operator.tsx.
   *
   * REQUIRED on purpose. Every page reaching this layout must name itself, and making the prop
   * mandatory hands that enforcement to the compiler: a new page that forgets fails
   * `tsc --noEmit` rather than shipping another tab labelled with the bare brand.
   */
  title: string
  children: React.ReactNode
}

// TEMP (hidden 2026-06-19): the notifications bell is hidden in both headers until we revisit it.
// Restore this helper together with the <NotificationBell> below and the `attentionCount` var.
// function scrollToAttention() {
//   const el = document.getElementById('needs-attention')
//   if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
// }

// The wordmark links to the briefing when we know which business we are in, and is a plain block
// when we do not — the frontend never assembles the identity part of a URL, so "no path served"
// means "no link", never a guessed one.
function LogoWrap({ href, children }: { href: string | null; children: React.ReactNode }) {
  const className = 'flex items-center gap-2.5'
  if (!href) return <div className={className}>{children}</div>
  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  )
}

export default function PortfolioLayout({ currentUser, properties, title, children }: PortfolioLayoutProps) {
  const brand = useBrand()
  // Code defensively: the backend props land concurrently, so every header-driving prop is
  // read with a safe default and never assumed present.
  const page = usePage<{
    currentAccount?: CurrentAccount | null
    canManageBilling?: boolean
    attentionCount?: number
    properties?: AccountPropertyGroup[]
    billingAlert?: BillingAlert | null
    spotlightAnnouncement?: AnnouncementData | null
  }>()

  // Prefer the explicit prop; fall back to the account-grouped `properties` shared prop so
  // any page using this layout (Billing, etc.) gets a working header without re-threading it.
  const groups: AccountPropertyGroup[] = properties ?? page.props.properties ?? []
  const currentAccount = page.props.currentAccount ?? null
  const canManageBilling = page.props.canManageBilling ?? false
  // TEMP (hidden 2026-06-19): used only by the hidden notifications bell — restore with it.
  // const attentionCount = page.props.attentionCount ?? 0

  // Guides are global and property-agnostic — served at /guides inside this same chrome.
  const helpHref = '/guides'

  return (
    <ToastProvider>
      <Head title={title} />
      <div className="min-h-screen bg-background">
        {/* Editorial top rule */}
        <header className="sticky top-0 z-30 border-b border-border/60 bg-white/85 backdrop-blur-md">
          {/* Inside the sticky header, above the header row: the strip travels with the chrome
              instead of scrolling away, and the header row never slides under it. Same component
              as the property shell — the header-chrome convention, one readout, two shells. */}
          <BillingAlertStrip alert={page.props.billingAlert} />
          <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-6 sm:px-8 lg:px-12">
            {/* The wordmark points at THIS business's briefing — `currentAccount.basePath`, served
                by the backend. Rendered inert rather than guessed when no account prop has landed. */}
            <LogoWrap href={currentAccount?.basePath ?? null}>
              <div className="flex h-8 w-8 flex-col items-center justify-center gap-[3px] rounded-lg bg-foreground" aria-hidden="true">
                <span className="h-[3px] w-4 rounded-full bg-white" />
                <span className="h-[3px] w-3 rounded-full bg-white/70" />
                <span className="h-[3px] w-2 rounded-full bg-white/40" />
              </div>
              <div className="leading-tight">
                <div className="text-lg font-semibold lowercase leading-none tracking-tight text-foreground">{brand}</div>
                <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Portfolio Briefing</div>
              </div>
            </LogoWrap>

            {/* Account context / scope switcher — always shows the account name as context;
                interactive only when the user spans more than one account. */}
            {groups.length > 0 && (
              <div className="hidden md:block">
                <AccountSwitcher properties={groups} currentAccount={currentAccount} mode="portfolio" />
              </div>
            )}

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              {/* Dry-run is a per-property flag, meaningless aggregated at the business level —
                  the dry-run badge lives on the per-property TopBar instead. */}

              {/* Scoped, searchable jump-to-property for the current business (⌘K) */}
              <JumpToProperty properties={groups} currentAccount={currentAccount} mode="portfolio" />

              {/* TEMP (hidden 2026-06-19): notifications bell hidden until we revisit it.
                  Restore this line together with scrollToAttention(), the attentionCount var, and the import above.
              <NotificationBell count={attentionCount} onClick={scrollToAttention} /> */}

              {/* Shared avatar + account menu — identical items to the per-property header
                  (billing, settings, help, log out). */}
              <UserMenu
                currentUser={currentUser}
                canManageBilling={canManageBilling}
                helpHref={helpHref}
                settingsHref={currentAccount?.settingsPath ?? null}
              />
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1400px] px-6 py-10 sm:px-8 lg:px-12 lg:py-14">
          <FlashHandler />
          {children}
        </main>

        <footer className="mt-16 border-t border-border/40">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-6 text-xs text-muted-foreground sm:px-8 lg:px-12">
            <span>{brand} · Revenue Management · {new Date().getFullYear()}</span>
            <span className="font-mono tabular-nums">v1</span>
          </div>
        </footer>

        {/* The Spotlight ships wherever a layout ships (§13.6) — including here, unlike the CARD,
            which is property-only because its CTA needs a hotel. A milestone's primary may be pure
            acknowledgement, so a briefing page is a perfectly good place to say it. No conditional
            against any other treatment: they never suppress each other (§13.2). */}
        {page.props.spotlightAnnouncement && (
          <SpotlightAnnouncement
            key={page.props.spotlightAnnouncement.id}
            announcement={page.props.spotlightAnnouncement}
          />
        )}
      </div>
    </ToastProvider>
  )
}
